import { Application } from 'pixi.js';
import { GameLoop } from './core/GameLoop';
import { SceneManager } from './core/SceneManager';
import { InputManager } from './input/InputManager';
import { PerfOverlay } from './debug/PerfOverlay';
import { DebugFlags } from './debug/DebugFlags';
import { RunScene } from './scenes/RunScene';
import { StressScene } from './scenes/StressScene';
import { MenuScene } from './scenes/MenuScene';
import { Profile } from './sim/meta/Profile';
import { LocalStorageProfileStore } from './meta/LocalStorageProfileStore';
import { FileProfileStore } from './meta/FileProfileStore';
import { Palette } from './render/Palette';
import { TextureFactory } from './render/TextureFactory';
import { loadPropTextures } from './render/props';
import { initJournal, logJournal } from './meta/journal';

async function boot(): Promise<void> {
  const app = new Application();
  await app.init({
    resizeTo: window,
    background: Palette.bg,
    antialias: true,
    autoStart: false, // OUR loop drives rendering, not Pixi's ticker
    // HiDPI: render at device resolution (capped — 3x mobile screens aren't
    // worth 9x the fill) and let autoDensity CSS-size the canvas back down.
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    autoDensity: true,
  });
  document.body.appendChild(app.canvas);

  const textures = new TextureFactory();
  textures.build(app.renderer);
  const props = await loadPropTextures();

  const input = new InputManager();
  input.attach(window);

  const scenes = new SceneManager();
  app.stage.addChild(scenes.root);

  const perf = new PerfOverlay(DebugFlags.debug || DebugFlags.stress || import.meta.env.DEV);
  app.stage.addChild(perf.root);

  const loop = new GameLoop(
    (dt) => {
      input.tick();
      scenes.update(dt, input.snapshot);
    },
    (alpha) => {
      scenes.render(alpha);
      app.render();
    },
  );
  loop.onFrameStats = (stats) => perf.record(stats);

  // Electron (the Deck build) provides a file-backed bridge via its preload;
  // the browser keeps localStorage. Same sync ProfileStore interface.
  const profile = new Profile(
    window.descentBridge
      ? new FileProfileStore(window.descentBridge)
      : new LocalStorageProfileStore(),
  );
  // Crash journal (Deck only — no-op without the bridge): coarse breadcrumbs
  // so a hard freeze can be read back from userData/journal.log next boot.
  initJournal(window.descentBridge);
  // Pad lifecycle breadcrumbs: "controls went unresponsive" (2026-07-27)
  // is most likely Steam Input re-grabbing the controller — make the
  // disconnect (or its absence) visible in the journal.
  window.addEventListener('gamepadconnected', (e) => {
    logJournal(`pad connected: ${(e as GamepadEvent).gamepad.id}`);
  });
  window.addEventListener('gamepaddisconnected', (e) => {
    logJournal(`pad disconnected: ${(e as GamepadEvent).gamepad.id}`);
  });
  if (import.meta.env.DEV) {
    (window as unknown as Record<string, unknown>)['__profile'] = profile;
    // Scripted playtests: rAF throttles in embedded/background panes, so
    // expose the loop for manual pumping (loop.step(1/60)) and the app for
    // forced resizes (Pixi's resizeTo flush also rides rAF).
    (window as unknown as Record<string, unknown>)['__loop'] = loop;
    (window as unknown as Record<string, unknown>)['__app'] = app;
    // Controller triage (e.g. Steam Deck stick dead): dump every pad the
    // browser sees while wiggling the stick to spot mapping/axis-index issues.
    (window as unknown as Record<string, unknown>)['__pads'] = () =>
      Array.from(navigator.getGamepads(), (p) =>
        p && {
          index: p.index,
          id: p.id,
          mapping: p.mapping,
          axes: p.axes.map((a) => Math.round(a * 100) / 100),
          pressed: p.buttons.flatMap((b, i) => (b.pressed ? [i] : [])),
        },
      );
  }
  const ctx = { app, loop, scenes, textures, props, perf, profile };
  if (DebugFlags.stress || DebugFlags.pattern) {
    const stress = new StressScene(app, loop, textures);
    perf.extra = () => stress.extraStats();
    scenes.push(stress);
  } else if (DebugFlags.seed || DebugFlags.floor) {
    // Debug fast path straight into a run.
    scenes.push(new RunScene(ctx));
  } else {
    scenes.push(new MenuScene(ctx));
  }

  loop.start();
}

void boot();
